import { createHeaders } from "ComponentHelpers/HeaderHelper";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "haunted";
import { removeTrailingSlash } from "Services/common";
import { API_TIMETABLE_URL } from "Services/constants";
import { useEffectWithoutCleanup } from "Shared/haunted/CustomHooks";
import { ApiSchedule } from "Shared/models/ApiSchedule";
import { ApiTimeTableV2 } from "Shared/models/ApiTimeTableV2";
import { Schedule } from "Shared/models/Schedule";
import { useReduxState } from "Shared/redux/useReduxState";

export interface Props {
	startLoading: () => LoaderInstance;
	stopLoading: (loader: LoaderInstance) => void;
}

interface TimetableQuerySignature {
	originCode: string | undefined;
	destinationCode: string | undefined;
	isOneWay: boolean;
}

export const useTimetable = (props: Props) => {
	const [currentSearch, setCurrentSearch] = useReduxState("currentSearch");

	const [navitaireSettings] = useReduxState("navitaireSettings");

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [disabledDepartureDates, setDisabledDepartureDates] = useState<dayjs.Dayjs[]>([]);
	const [disabledReturnDates, setDisabledReturnDates] = useState<dayjs.Dayjs[]>([]);
	const [maxDepartureDate, setMaxDepartureDate] = useState<dayjs.Dayjs>(undefined);
	const [maxReturnDate, setMaxReturnDate] = useState<dayjs.Dayjs>(undefined);
	const [minDepartureDate, setMinDepartureDate] = useState<dayjs.Dayjs>(undefined);
	const [minReturnDate, setMinReturnDate] = useState<dayjs.Dayjs>(undefined);
	const [outboundAvailableDates, setOutboundAvailableDates] = useState<Schedule[]>([]);
	const [inboundAvailableDates, setInboundAvailableDates] = useState<Schedule[]>([]);
	const [lastQueriedTimetable, setLastQueriedTimetable] = useState<TimetableQuerySignature>({
		originCode: undefined,
		destinationCode: undefined,
		isOneWay: false,
	});

	const findMissingDaysBetween = (days: dayjs.Dayjs[], minDate: dayjs.Dayjs, maxDate: dayjs.Dayjs) => {
		const missingDays: dayjs.Dayjs[] = [];

		let currentDate = minDate.startOf("day");
		while (currentDate.isSameOrBefore(maxDate)) {
			if (!days.some((day) => day.isSame(currentDate, "day"))) {
				missingDays.push(currentDate);
			}
			currentDate = currentDate.add(1, "day");
		}

		return missingDays;
	};

	const maxBy = <T, U>(array: T[], selector: (element: T) => U): T | undefined => {
		if (array.length === 0) return undefined;

		let maxElement = array[0];
		let maxValue = selector(maxElement);

		for (let i = 1; i < array.length; i++) {
			const value = selector(array[i]);

			if (value > maxValue) {
				maxElement = array[i];
				maxValue = value;
			}
		}

		return maxElement;
	};

	const minBy = <T, U>(array: T[], selector: (element: T) => U): T | undefined => {
		if (array.length === 0) return undefined;

		let minElement = array[0];
		let minValue = selector(minElement);

		for (let i = 1; i < array.length; i++) {
			const value = selector(array[i]);

			if (value < minValue) {
				minElement = array[i];
				minValue = value;
			}
		}

		return minElement;
	};

	const digestTimetable = async (info: Schedule[]) => {
		let disabled: dayjs.Dayjs[] = [];

		info = info.sort((a, b) => (a.departureDate.isAfter(b.departureDate) ? 1 : -1));

		const min = minBy(info, (i) => i.departureDate.unix())?.departureDate;
		const max = maxBy(info, (i) => i.departureDate.unix())?.departureDate;

		disabled = findMissingDaysBetween(
			info.map((i): Dayjs => i.departureDate),
			min || dayjs(),
			max
		);

		return { min: min || dayjs(), max: max || dayjs(), disabled };
	};

	const createTimetableUrl = () => {
		const startOfMonth = dayjs().startOf("month").format("YYYY-MM-DD");
		const shouldSearchOneWayOnly = !window.JetSmart.TimetableOptions.IncludeReturnFlights || currentSearch.isOneWay;

		if (window.JetSmart.TimetableOptions.Mode === "DirectFareCache") {
			const baseUrl = removeTrailingSlash(window.JetSmart.TimetableOptions.BaseAddress);

			return [
				`${baseUrl}/timetable?departure=${currentSearch.originCity.code}`,
				`destination=${currentSearch.destinationCity.code}`,
				`currency=${navitaireSettings?.currency}`,
				`withPriceRange`,
				`startDate=${startOfMonth}`,
				`${shouldSearchOneWayOnly ? "return=false" : ""}`,
			]
				.filter((item) => item)
				.join("&");
		}

		return [
			`${API_TIMETABLE_URL}?departure=${currentSearch.originCity.code}`,
			`destination=${currentSearch.destinationCity.code}`,
			`currency=${navitaireSettings?.currency}`,
			`startDate=${startOfMonth}`,
			`${shouldSearchOneWayOnly ? "return=false" : ""}`,
		]
			.filter((item) => item)
			.join("&");
	};

	const mapToOffer = (o: ApiSchedule): Schedule => ({
		currency: o.currency,
		departureDate: dayjs(o.departureDate),
		price: o.price,
		priceCategory: o.priceCategory,
	});

	const constrainDepartureDates = async (schedule: Schedule[]) => {
		const outbound = await digestTimetable(schedule);

		setDisabledDepartureDates(outbound.disabled);
		setMaxDepartureDate(dayjs(outbound.max));
		setMinDepartureDate(dayjs(outbound.min));
	};

	const constrainReturnDates = async (schedule: Schedule[]) => {
		const inbound = await digestTimetable(schedule);

		const isOutboundDateLaterThanMinInboundDate =
			currentSearch.departureDate && inbound.min.isSameOrBefore(currentSearch.departureDate, "day");

		setDisabledReturnDates(inbound.disabled);
		setMaxReturnDate(dayjs(inbound.max));
		setMinReturnDate(
			isOutboundDateLaterThanMinInboundDate ? dayjs(currentSearch.departureDate) : dayjs(inbound.min)
		);
	};

	const update = async () => {
		if (!currentSearch.originCity || !currentSearch.destinationCity) return;

		const newQuerySignature: TimetableQuerySignature = {
			originCode: currentSearch.originCity.code,
			destinationCode: currentSearch.destinationCity.code,
			isOneWay: currentSearch.isOneWay,
		};

		const isSameQuerySignature =
			lastQueriedTimetable.originCode === newQuerySignature.originCode &&
			lastQueriedTimetable.destinationCode === newQuerySignature.destinationCode &&
			lastQueriedTimetable.isOneWay === newQuerySignature.isOneWay;

		if (isSameQuerySignature) return;

		setLastQueriedTimetable(newQuerySignature);

		if (!currentSearch.isReloading) {
			setCurrentSearch({
				...currentSearch,
				departureDate: undefined,
				returnDate: undefined,
			});
		}

		setIsLoading(true);
		const loader = props.startLoading();

		const timetable = await loadTimetable();

		if (!timetable) {
			// eslint-disable-next-line no-console
			console.error("Failed to load timetable data");
			setIsLoading(false);
			props.stopLoading(loader);
			return;
		}

		setOutboundAvailableDates(timetable.outbound);
		setInboundAvailableDates(timetable.inbound);

		await constrainDepartureDates(timetable.outbound);
		if (!currentSearch.isOneWay) await constrainReturnDates(timetable.inbound);

		setIsLoading(false);
		props.stopLoading(loader);

		setCurrentSearch({ ...currentSearch, isReloading: false });
	};

	const loadTimetable = async () => {
		const headers = createHeaders();

		const url = createTimetableUrl();
		const fetchOptions: RequestInit = {
			method: "GET",
			mode: "cors",
			headers,
		};

		try {
			const result = await fetch(url, fetchOptions);

			if (result.ok) {
				const resultJson = (await result.json()) as ApiTimeTableV2;

				return {
					inbound: resultJson.inbound?.map(mapToOffer) || [],
					outbound: resultJson.outbound?.map(mapToOffer) || [],
				};
			}

			return null;
		} catch (e) {
			return null;
		}
	};

	useEffectWithoutCleanup(update, [
		currentSearch.originCity?.code,
		currentSearch.destinationCity?.code,
		currentSearch.isOneWay,
	]);

	useEffectWithoutCleanup(() => {
		if (currentSearch.isOneWay) return;
		if (!currentSearch.departureDate || inboundAvailableDates.length === 0) return;
		void constrainReturnDates(inboundAvailableDates);
	}, [currentSearch.departureDate, currentSearch.isOneWay, inboundAvailableDates]);

	return {
		disabledDepartureDates,
		disabledReturnDates,
		isLoading,
		maxDepartureDate,
		maxReturnDate,
		minDepartureDate,
		minReturnDate,
		outboundAvailableDates,
		inboundAvailableDates,
		selectedPriceOutbound:
			outboundAvailableDates.find((schedule) => schedule.departureDate.isSame(currentSearch.departureDate, "day"))
				?.price || "",
		selectedPriceInbound:
			inboundAvailableDates.find((schedule) => schedule.departureDate.isSame(currentSearch.returnDate, "day"))
				?.price || "",
		setMinReturnDate: (date: dayjs.Dayjs) => setMinReturnDate(date),
	};
};
